import Papa from 'papaparse';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { Directionality, ScaleTypes, UnitType } from 'BMapsSrc/integrations/properties_common';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';
import { gninaProperties } from './GninaScoringJob';

const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            library_screen: {
                metadata: {
                    label: 'Screening properties',
                    tooltipHeading: 'Screening properties',
                },
                properties: {
                    boltz_affinity__affinity_pred_value: {
                        metadata: {
                            label: 'Affinity (Boltz-2)',
                            description: "Boltz-2's predicted affinity of an active ligand to the target, from the ensemble model. Suggested for hit-to-lead or lead-optimization.",
                            unit: 'log(IC50) μM',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultVisible: true,
                            defaultThresholds: { max: -1 },
                        },
                    },
                    boltz_affinity__affinity_probability_binary: {
                        metadata: {
                            label: 'Binding Probability (Boltz-2)',
                            description: "Boltz-2's predicted probability that a ligand binds to the target, from the ensemble model. Suggested for hit identification.",
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultVisible: true,
                            defaultThresholds: { min: 0.50 },
                            defaultActive: true,
                        },
                    },
                    boltz_affinity__affinity_pred_value1: {
                        metadata: {
                            label: 'Affinity (Model #1) (Boltz-2)',
                            description: "Boltz-2's predicted affinity of an active ligand to the target, from the first model in the ensemble.",
                            unit: 'log(IC50) μM',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { max: -1 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_probability_binary1: {
                        metadata: {
                            label: 'Binding Probability (Model #1) (Boltz-2)',
                            description: "Boltz-2's predicted probability that a ligand binds to the target, from the first model in the ensemble.",
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { min: 0.50 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_pred_value2: {
                        metadata: {
                            label: 'Affinity (Model #2) (Boltz-2)',
                            description: "Boltz-2's predicted affinity of an active ligand to the target, from the second model in the ensemble.",
                            unit: 'log(IC50) μM',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { max: -1 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_probability_binary2: {
                        metadata: {
                            label: 'Binding Probability (Model #2) (Boltz-2)',
                            description: "Boltz-2's predicted probability that a ligand binds to the target, from the second model in the ensemble.",
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { min: 0.50 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_derived_ic50: {
                        metadata: {
                            label: 'IC50 μM (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction",
                            unit: 'μM',
                            unitType: UnitType.POSITIVE,
                            precision: 3,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultThresholds: { max: 0.1 },
                        },
                    },
                    boltz_affinity__affinity_derived_ic501: {
                        metadata: {
                            label: 'IC50 μM (Model #1) (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction from the first model in the ensemble.",
                            unit: 'μM',
                            unitType: UnitType.POSITIVE,
                            precision: 3,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultThresholds: { max: 0.1 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_derived_ic502: {
                        metadata: {
                            label: 'IC50 μM (Model #2) (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction from the second model in the ensemble.",
                            unit: 'μM',
                            unitType: UnitType.POSITIVE,
                            precision: 3,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultThresholds: { max: 0.1 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_derived_ic50_nM: {
                        metadata: {
                            label: 'IC50 nM (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction",
                            unit: 'nM',
                            unitType: UnitType.POSITIVE,
                            precision: 1,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultVisible: true,
                            defaultThresholds: { max: 100 },
                        },
                    },
                    boltz_affinity__affinity_derived_ic501_nM: {
                        metadata: {
                            label: 'IC50 nM (Model #1) (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction from the first model in the ensemble.",
                            unit: 'nM',
                            unitType: UnitType.POSITIVE,
                            precision: 1,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultThresholds: { max: 100 },
                            advanced: true,
                        },
                    },
                    boltz_affinity__affinity_derived_ic502_nM: {
                        metadata: {
                            label: 'IC50 nM (Model #2) (Boltz-2)',
                            description: "Predicted IC50 based on Boltz-2's affinity prediction from the second model in the ensemble.",
                            unit: 'nM',
                            unitType: UnitType.POSITIVE,
                            precision: 1,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultScale: ScaleTypes.LOGARITHMIC,
                            defaultThresholds: { max: 100 },
                            advanced: true,
                        },
                    },

                    boltz_confidence__confidence_score: {
                        metadata: {
                            label: 'Boltz-2 Confidence Score',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultVisible: true,
                        },
                    },
                    boltz_confidence__ptm: {
                        metadata: {
                            label: 'pTM (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__iptm: {
                        metadata: {
                            label: 'ipTM (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__ligand_iptm: {
                        metadata: {
                            label: 'Ligand ipTM (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__protein_iptm: {
                        metadata: {
                            label: 'Protein ipTM (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__complex_plddt: {
                        metadata: {
                            label: 'Complex pLDDT (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__complex_iplddt: {
                        metadata: {
                            label: 'Complex ipLDDT (Boltz-2)',
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__complex_pde: {
                        metadata: {
                            label: 'Complex pDE (Boltz-2)',
                            unit: 'Å',
                            unitType: UnitType.POSITIVE,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    boltz_confidence__complex_ipde: {
                        metadata: {
                            label: 'Complex ipDE (Boltz-2)',
                            unit: 'Å',
                            unitType: UnitType.POSITIVE,
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },

                    bfd__vdw: {
                        metadata: {
                            label: 'vdW (Amber)',
                            description: 'Calculated van der Waals interaction energy between ligand and receptor',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { max: 0 },
                            advanced: true,
                        },
                    },
                    bfd__electrostatics: {
                        metadata: {
                            label: 'Electrostatics (Amber)',
                            description: 'Calculated electrostatic interaction energy between ligand and receptor',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            advanced: true,
                        },
                    },
                    bfd__interaction_total: {
                        metadata: {
                            label: 'Interaction Energy (Amber)',
                            description: 'Sum of vdW and electrostatic energies between ligand and receptor',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultVisible: true,
                            defaultThresholds: { max: 0 },
                        },
                    },
                    bfd__stress: {
                        metadata: {
                            label: 'Stress Energy (Amber)',
                            description: 'Calculated internal strain energy within the ligand',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    bfd__ddGs: {
                        metadata: {
                            label: 'ΔΔGs (BMaps)',
                            description: 'Predicted cost of solvation',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    bfd__amber_total: {
                        metadata: {
                            label: 'Total Amber Energy',
                            description: 'Sum of interaction and internal (stress) energies for the bound ligand pose.\nThis value may be less predictive than the interaction energy.',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { max: 0 },
                            advanced: true,
                        },
                    },
                    bfd__energy_sum: {
                        metadata: {
                            label: 'BMaps Energy Total',
                            description: 'Sum of interaction energy, internal (stress) energy, and desolvation cost (ΔΔGs) for the bound ligand pose.\nThis value may be less predictive than the interaction energy.',
                            unit: 'kcal/mol',
                            directionality: Directionality.MINIMIZE,
                            propApplicability: PropApplicability.PERCOMPOUND,
                            defaultThresholds: { max: 0 },
                            advanced: true,
                        },
                    },

                    gnina__minimizedAffinity: {
                        metadata: {
                            ...gninaProperties.affinity.metadata,
                            label: 'Affinity (Gnina)',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    gnina__CNNscore: {
                        metadata: {
                            ...gninaProperties.cnn_score.metadata,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    gnina__CNNaffinity: {
                        metadata: {
                            ...gninaProperties.cnn_affinity.metadata,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    gnina__CNN_VS: {
                        metadata: {
                            ...gninaProperties.cnn_vs.metadata,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    gnina__CNNaffinity_variance: {
                        metadata: {
                            ...gninaProperties.cnn_variance.metadata,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                },
            },
        },
    },
});

/**
 * Library screening job (long-running): parses the per-compound screening
 * CSV results, derives additional combined/converted properties, and stores
 * the assembled results on local job state for the screening views.
 *
 * @augments JobDefinition
 */
export class LibraryScreeningJob extends JobDefinition {
    static jobTypeId = JobTypes.LIBRARY_SCREEN;
    static resultDestination = JobResultDestinations.SCREENING;
    static jobTypeName = 'Library Screening';
    static jobName = 'Library Screening Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /**
     * Parses each compound's screening CSV, derives combined energy and
     * IC50-conversion columns, and stores the assembled results on local job
     * state.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        function deriveProps(properties) {
            const derivedProps = {};
            // Derive nM values from μM IC50 values
            for (const [key, value] of Object.entries(properties)) {
                if (key.startsWith('boltz_affinity__affinity_derived_ic50')) {
                    const derivedKey = `${key}_nM`;
                    derivedProps[derivedKey] = null; // reserve column slot for ordering
                    if (value != null) derivedProps[derivedKey] = value * 1000;
                }
            }
            // Derive combined energy values from individual BFD energies
            const vdW = properties['bfd__vdw'];
            const electrostatics = properties['bfd__electrostatics'];
            const stress = properties['bfd__stress'];
            const ddGs = properties['bfd__ddGs'];
            derivedProps['bfd__interaction_total'] = null;
            derivedProps['bfd__amber_total'] = null;
            derivedProps['bfd__energy_sum'] = null;
            if (vdW != null || electrostatics != null) {
                const interactionTotal = (vdW ?? 0) + (electrostatics ?? 0);
                derivedProps['bfd__interaction_total'] = interactionTotal;
                if (stress != null) {
                    const amberTotal = interactionTotal + stress;
                    derivedProps['bfd__amber_total'] = amberTotal;
                    if (ddGs != null) {
                        derivedProps['bfd__energy_sum'] = amberTotal + ddGs;
                    }
                }
            }
            return derivedProps;
        }

        const res = [];
        let compounds = results?.jobResults || [];
        if (typeof compounds === 'string') {
            compounds = JSON.parse(compounds);
        }
        for (const { cmpd_id: name, smiles, csv } of compounds) {
            if (csv) {
                const properties = Papa.parse(
                    csv, { header: true, dynamicTyping: true, skipEmptyLines: true }
                ).data;
                const resultsData = { structure: smiles, name };
                let result = res.find((r) => r.structure === resultsData.structure);
                for (const row of properties) {
                    for (const [key, value] of Object.entries(row)) {
                        if (key === 'library_item_id') continue;
                        if (!result) {
                            result = {
                                structure: resultsData.structure,
                                name: resultsData.name,
                                properties: {},
                            };
                            res.push(result);
                        }
                        const prevEntry = result.properties[row.library_item_id];
                        result.properties[row.library_item_id] = {
                            ...prevEntry,
                            [key]: value,
                        };
                    }
                    if (result) {
                        Object.assign(result.properties[row.library_item_id], deriveProps(row));
                    }
                }
            }
        }
        this.results = res;
        updateLocalJobState(job.jobid, { results: res });
        return { results };
    }
}
